import React, { useEffect, useState } from 'react';
import AuthUser from './AuthUser';
import toast, { Toaster } from 'react-hot-toast';
import useTeacherClassScope from '../hooks/useTeacherClassScope';

const TITLE_MAX = 150;
const DESCRIPTION_MAX = 1000;

export default function HomeworkList() {
    const { http, user } = AuthUser();
    const scope = useTeacherClassScope(http, user);
    const [homeworks, setHomeworks] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [detailItem, setDetailItem] = useState(null);
    const [formData, setFormData] = useState({
        subject: '',
        title: '',
        description: '',
        due_date: '',
    });

    useEffect(() => {
        fetchSubjects();
        fetchHomeworks();
    }, []);

    const fetchSubjects = async () => {
        try {
            const subjectRes = await http.get(`/subject?login_user=${user.id}`);
            setSubjects(Array.isArray(subjectRes.data?.data) ? subjectRes.data.data : []);
        } catch (error) {
            console.error('Error fetching subjects:', error);
            toast.error('Could not load subject list');
        }
    };

    const fetchHomeworks = async () => {
        setLoading(true);
        try {
            const response = await http.get(`/homeworks?company_id=${user.company_id}`);
            if (response.data.success) {
                setHomeworks(response.data.data || []);
            } else {
                toast.error(response.data.message || 'Failed to load homework');
            }
        } catch (error) {
            console.error('Error fetching homework:', error);
            toast.error(error.response?.data?.message || 'Error fetching homework');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!scope.canSelectClassDivision) {
            toast.error('No class/division assigned to you');
            return;
        }
        if (!scope.selectedClass) {
            toast.error('Please select class');
            return;
        }
        if (!scope.selectedDivision) {
            toast.error('Please select division');
            return;
        }
        if (!formData.subject) {
            toast.error('Please select subject');
            return;
        }
        if (!formData.title.trim()) {
            toast.error('Title is required');
            return;
        }
        if (!formData.description.trim()) {
            toast.error('Homework details are required');
            return;
        }
        if (formData.title.length > TITLE_MAX) {
            toast.error(`Title must be ${TITLE_MAX} characters or less`);
            return;
        }
        if (formData.description.length > DESCRIPTION_MAX) {
            toast.error(`Details must be ${DESCRIPTION_MAX} characters or less`);
            return;
        }

        setSending(true);
        try {
            const response = await http.post('/homeworks', {
                company_id: user.company_id,
                class: scope.selectedClass,
                division: scope.selectedDivision,
                subject: formData.subject,
                title: formData.title.trim(),
                description: formData.description.trim(),
                due_date: formData.due_date || null,
                login_user: user.id,
            });

            if (response.data.success) {
                toast.success(response.data.message || 'Homework saved!');
                setFormData({
                    subject: '',
                    title: '',
                    description: '',
                    due_date: '',
                });
                scope.resetClassDivisionAfterSend();
                fetchHomeworks();
            } else {
                toast.error(response.data.message || 'Failed to save');
            }
        } catch (error) {
            console.error('Error saving homework:', error);
            toast.error(error.response?.data?.message || 'Error saving homework');
        } finally {
            setSending(false);
        }
    };

    const formatDate = (value) => {
        if (!value) return '—';
        try {
            const d = new Date(value);
            if (Number.isNaN(d.getTime())) return value;
            return d.toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return value;
        }
    };

    const formatDay = (value) => {
        if (!value) return '—';
        try {
            const d = new Date(value);
            if (Number.isNaN(d.getTime())) return value;
            return d.toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
            });
        } catch {
            return value;
        }
    };

    return (
        <>
            <Toaster />
            <div className="container-fluid mt-4 px-4">
                <div className="row mb-4">
                    <div className="col-12">
                        <h3 className="mb-1">Homework</h3>
                        <p className="text-muted mb-0">
                            Assign homework by class, division and subject. Parents see it in the mobile app.
                            {!scope.isSuperAdmin && (
                                <span className="ms-1">(Your assigned class/division only)</span>
                            )}
                        </p>
                    </div>
                </div>

                <div className="card mb-4">
                    <div className="card-header">
                        <h5 className="mb-0">Add Homework</h5>
                    </div>
                    <div className="card-body">
                        {!scope.isSuperAdmin && !scope.loadingScope && scope.assignmentCount === 0 && (
                            <p className="text-muted small mb-3">
                                No class/division assigned yet. Ask admin to use Admin → Assign Teacher.
                            </p>
                        )}
                        <form onSubmit={handleSubmit}>
                            <div className="row g-3">
                                <div className="col-md-3">
                                    <label className="form-label">Class</label>
                                    <select
                                        name="class"
                                        className="form-control"
                                        value={scope.selectedClass}
                                        onChange={(e) => scope.onClassChange(e.target.value)}
                                        disabled={scope.loadingScope || scope.fieldsLocked}
                                    >
                                        <option value="">Select Class</option>
                                        {scope.classOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">Division</label>
                                    <select
                                        name="division"
                                        className="form-control"
                                        value={scope.selectedDivision}
                                        onChange={(e) => scope.onDivisionChange(e.target.value)}
                                        disabled={scope.loadingScope || scope.fieldsLocked}
                                    >
                                        <option value="">Select Division</option>
                                        {scope.divisionOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">Subject</label>
                                    <select
                                        name="subject"
                                        className="form-control"
                                        value={formData.subject}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select Subject</option>
                                        {subjects.map((option) => (
                                            <option key={option.id} value={option.name}>
                                                {option.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">Due Date (optional)</label>
                                    <input
                                        type="date"
                                        name="due_date"
                                        className="form-control"
                                        value={formData.due_date}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">
                                        Title{' '}
                                        <span className="text-muted">
                                            ({formData.title.length}/{TITLE_MAX})
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        name="title"
                                        className="form-control"
                                        value={formData.title}
                                        onChange={handleChange}
                                        maxLength={TITLE_MAX}
                                        placeholder="Homework title"
                                    />
                                </div>
                                <div className="col-12">
                                    <label className="form-label">
                                        Homework details{' '}
                                        <span className="text-muted">
                                            ({formData.description.length}/{DESCRIPTION_MAX})
                                        </span>
                                    </label>
                                    <textarea
                                        name="description"
                                        className="form-control"
                                        rows={4}
                                        value={formData.description}
                                        onChange={handleChange}
                                        maxLength={DESCRIPTION_MAX}
                                        placeholder="Write the homework details..."
                                    />
                                </div>
                                <div className="col-12">
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={sending || !scope.canSelectClassDivision}
                                    >
                                        {sending ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm me-2"
                                                    role="status"
                                                />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-save me-1"></i> Save Homework
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Homework List</h5>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={fetchHomeworks}
                            disabled={loading}
                        >
                            <i className="fa fa-refresh me-1"></i> Refresh
                        </button>
                    </div>
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-4">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : homeworks.length === 0 ? (
                            <div className="text-center py-4">
                                <p className="text-muted mb-0">
                                    No homework added yet. Use the form above to add one.
                                </p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: 50 }}>#</th>
                                            <th>Date</th>
                                            <th>Class</th>
                                            <th>Division</th>
                                            <th>Subject</th>
                                            <th>Title</th>
                                            <th>Due</th>
                                            <th>Added By</th>
                                            <th style={{ width: 90 }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {homeworks.map((item, index) => (
                                            <tr key={item.id}>
                                                <td>{index + 1}</td>
                                                <td className="text-nowrap">
                                                    {formatDate(item.created_at)}
                                                </td>
                                                <td>{item.class}</td>
                                                <td>{item.division}</td>
                                                <td>{item.subject}</td>
                                                <td>
                                                    <strong>{item.title}</strong>
                                                </td>
                                                <td className="text-nowrap">
                                                    {formatDay(item.due_date)}
                                                </td>
                                                <td>{item.sent_by || '—'}</td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={() => setDetailItem(item)}
                                                    >
                                                        View
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {detailItem && (
                <div
                    className="modal fade show"
                    style={{ display: 'block', background: 'rgba(0,0,0,0.45)' }}
                    tabIndex={-1}
                    role="dialog"
                >
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{detailItem.title}</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setDetailItem(null)}
                                />
                            </div>
                            <div className="modal-body">
                                <div className="row g-2 mb-3">
                                    <div className="col-md-3">
                                        <small className="text-muted d-block">Class</small>
                                        <strong>{detailItem.class}-{detailItem.division}</strong>
                                    </div>
                                    <div className="col-md-3">
                                        <small className="text-muted d-block">Subject</small>
                                        <strong>{detailItem.subject}</strong>
                                    </div>
                                    <div className="col-md-3">
                                        <small className="text-muted d-block">Due Date</small>
                                        <strong>{formatDay(detailItem.due_date)}</strong>
                                    </div>
                                    <div className="col-md-3">
                                        <small className="text-muted d-block">Added By</small>
                                        <strong>{detailItem.sent_by || '—'}</strong>
                                    </div>
                                </div>
                                <small className="text-muted d-block mb-1">Details</small>
                                <div
                                    className="border rounded p-3 bg-light"
                                    style={{ whiteSpace: 'pre-wrap' }}
                                >
                                    {detailItem.description}
                                </div>
                                <div className="mt-3 text-muted small">
                                    Created: {formatDate(detailItem.created_at)}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setDetailItem(null)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
